import { useEffect, useContext, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { useGetData } from "../../../../../hooks/useGetData";
import { useMutationData } from "../../../../../hooks/useMutationData";
import { EvaluadoInterface } from "../../../../../interfaces/EvaluadoInterface";
import { FechasInterface } from "../../../../../interfaces/FechasInterface";
import { AutomovilesBienes } from "../../../../../interfaces/AutomovilesBienesInterface";
import { BienesRaices } from "../../../../../interfaces/BienesRaicesInterface";
import { NotasBienes } from "../../../../../interfaces/NotasBienesInterface";
import { SituacionInmuebleInterface } from "../../../../../interfaces/SituacionInmuebleInterface";
import { ZonaInterface } from "../../../../../interfaces/ZonaInterface";
import { TipoZonaInterface } from "../../../../../interfaces/TipoZonaInterface";
import { Loading } from "../../../../../components/Loading/Loading";
import { Button, Checkbox, Label, Textarea, TextInput } from "flowbite-react";
import SkyClear from "../../../../../img/SkyClear.png";
import { ThemeContext } from "../../../../../components/ThemeContext/ThemeContext";
import Header from "../../../../../components/HeaderForms/HeaderForms";

export const BienesEditarScreen = () => {
  const tituloFormulario = "BIENES INMUEBLES / MUEBLES";
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [sexo, setSexo] = useState("");
  const { evaluadosList_id } = useParams<{ evaluadosList_id: string }>();
  const { control, reset, handleSubmit, setValue } = useForm();
  const { isDarkMode } = useContext(ThemeContext);
  const [otroSexo, setOtroSexo] = useState("");
  const [edad, setEdad] = useState<number | null>(null);
  const [fechaNacimiento, setFechaNacimiento] = useState<string>("");
  const [formData, setFormData] = useState({});

  const [autos, setAutos] = useState<
    {
      marca: string;
      modelo: string;
      precioEstimado: string;
      liquidadoA: string;
    }[]
  >([{ marca: "", modelo: "", precioEstimado: "", liquidadoA: "" }]);

  const [bienesRaices, setBienesRaices] = useState<
    {
      tipo: string;
      ubicacion: string;
      precioEstimado: string;
      liquidadoA: string;
    }[]
  >([{ tipo: "", ubicacion: "", precioEstimado: "", liquidadoA: "" }]);

  const generoMap: { [key: string]: string } = {
    M: "Masculino",
    F: "Femenino",
    O: "Otro",
  };

  const [situacionSeleccionada, setSituacionSeleccionada] = useState<
    string | null
  >(null);
  const [nivelSocioeconomicoSeleccionado, setNivelSocioeconomicoSeleccionado] =
    useState<string | null>(null);
  const [tipoZonaSeleccionado, setTipoZonaSeleccionado] = useState<
    string | null
  >(null);

  const calcularEdad = (fechaNacimiento: string) => {
    const fechaNac = new Date(fechaNacimiento);
    const hoy = new Date();
    let edadCalculada = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
      edadCalculada--;
    }

    return edadCalculada;
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setFormData((prevData) => ({ ...prevData, [name]: checked }));
  };

  const handlePageChange = (newPage: number) => {
    const filteredAutos = autos
      .slice(1)
      .filter(
        (auto) =>
          auto.marca.trim() !== "" &&
          auto.modelo.trim() !== "" &&
          auto.precioEstimado.trim() !== "" &&
          auto.liquidadoA.trim() !== ""
      );

    const filteredBienesRaices = bienesRaices
      .slice(1)
      .filter(
        (bien) =>
          bien.tipo.trim() !== "" &&
          bien.ubicacion.trim() !== "" &&
          bien.precioEstimado.trim() !== "" &&
          bien.liquidadoA.trim() !== ""
      );

    const updatedAutos = [autos[0], ...filteredAutos];
    const updatedBienesRaices = [bienesRaices[0], ...filteredBienesRaices];

    if (JSON.stringify(updatedAutos) !== JSON.stringify(autos)) {
      setAutos(updatedAutos);
    }
    if (JSON.stringify(updatedBienesRaices) !== JSON.stringify(bienesRaices)) {
      setBienesRaices(updatedBienesRaices);
    }

    setCurrentPage(newPage);
  };

  const handleFechaNacimientoChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const nuevaFechaNacimiento = event.target.value;
    setFechaNacimiento(nuevaFechaNacimiento);

    const nuevaEdad = calcularEdad(nuevaFechaNacimiento);
    setEdad(nuevaEdad);
  };

  const handleSexoChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSexo(value);
    if (value !== "Otro") {
      setOtroSexo("");
    }
  };

  const handleAutoChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    const updatedAutos = autos.map((auto, i) =>
      i === index ? { ...auto, [name]: value } : auto
    );
    setAutos(updatedAutos);
  };

  const handleBienesRaicesChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    const updatedBienesRaices = bienesRaices.map((bien, i) =>
      i === index ? { ...bien, [name]: value } : bien
    );
    setBienesRaices(updatedBienesRaices);
  };

  const handleAddBienRaices = () => {
    setBienesRaices([
      ...bienesRaices,
      { tipo: "", ubicacion: "", precioEstimado: "", liquidadoA: "" },
    ]);
  };

  const handleSituacionChange = (option: string) => {
    setSituacionSeleccionada(option);
  };

  const handleNivelSocioeconomicoChange = (option: string) => {
    setNivelSocioeconomicoSeleccionado(option);
  };

  const handleTipoZonaChange = (option: string) => {
    setTipoZonaSeleccionado(option);
  };

  const renderHeader = () => (
    <>
      {/* Header */}
      <Header titulo={tituloFormulario} />
    </>
  );

  const handlePrint = () => { 
    const printContent = document.getElementById("printableContent");

    if (printContent) {
      const printWindow = window.open("", "", "width=800,height=600");

      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Vista Previa de Impresión</title>
              <style>
                @media print {
                  body {
                    margin: 20px;
                    font-family: Arial, sans-serif;
                  }
                }
              </style>
            </head>
            <body>${printContent.innerHTML}</body>
          </html>
        `);

        const styles = document.styleSheets;
        for (let i = 0; i < styles.length; i++) {
          try {
            if (styles[i].cssRules) {
              const styleTag = printWindow.document.createElement("style");
              for (let j = 0; j < styles[i].cssRules.length; j++) {
                styleTag.appendChild(
                  printWindow.document.createTextNode(
                    styles[i].cssRules[j].cssText
                  )
                );
              }
              printWindow.document.head.appendChild(styleTag);
            }
          } catch (e) {
            console.warn("No se pudo copiar una hoja de estilo:", e);
          }
        }

        printWindow.document.close();
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            printWindow.close();
          }, 500);
        };
      } else {
        alert("No se pudo abrir la ventana de impresión.");
      }
    }
  };

  const queryEvaluado = useGetData<EvaluadoInterface>({
    url: `/evaluado/${evaluadosList_id}`,
    name: ["evaluado"],
  });

  const mutateFecha = useMutationData({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    reload: ["evaluado", `${evaluadosList_id}`, "fecha-visita-bienesinmuebles"],
  });

  const queryFecha = useGetData<FechasInterface[]>({
    url: `/evaluado/${evaluadosList_id}/fecha-visita-bienesinmuebles`,
    name: ["fecha-visita-bienesinmuebles"],
  });

  const queryAuto = useGetData<AutomovilesBienes[]>({
    url: `/autobienes/${evaluadosList_id}`,
    name: ["autobienes"],
  });

  const mutateAutomovilBienes = useMutationData({
    url: `/autobienes/${evaluadosList_id}`,
    reload: ["evaluado", "autobienes", `${evaluadosList_id}`],
  });

  const queryBienesRaices = useGetData<BienesRaices[]>({
    url: `/bienesraices/${evaluadosList_id}`,
    name: ["bienesraices"],
  });

  const mutateBienesRaices = useMutationData({
    url: `/bienesraices/${evaluadosList_id}`,
    reload: ["evaluado", "bienesraices", `${evaluadosList_id}`],
  });

  const queryNotasBienes = useGetData<NotasBienes[]>({
    url: `/notas-bienes-inmuebles/${evaluadosList_id}`,
    name: ["notas-bienes-inmuebles"],
  });

  const mutateNotasBienes = useMutationData({
    url: `/notas-bienes-inmuebles/${evaluadosList_id}`,
    reload: ["evaluado", "notasbienes", `${evaluadosList_id}`],
  });

  const querySituacionInmueble = useGetData<SituacionInmuebleInterface[]>({
    url: `/situacion-inmueble/${evaluadosList_id}`,
    name: ["situacion-inmueble"],
  });

  const mutateSituacionInmueble = useMutationData({
    url: `/situacion-inmueble/${evaluadosList_id}`,
    reload: ["evaluado", "situacioninmueble", `${evaluadosList_id}`],
  });

  const queryZonaInmueble = useGetData<ZonaInterface[]>({
    url: `/zona-inmueble/${evaluadosList_id}`,
    name: ["zona-inmueble"],
  });

  const mutateZonaInmueble = useMutationData({
    url: `/zona-inmueble/${evaluadosList_id}`,
    reload: ["evaluado", "zonainmueble", `${evaluadosList_id}`],
  });

  const queryTipoZona = useGetData<TipoZonaInterface[]>({
    url: `/tipo-zona/${evaluadosList_id}`,
    name: ["tipo-zona"],
  });

  const mutateTipoZona = useMutationData({
    url: `/tipo-zona/${evaluadosList_id}`,
    reload: ["evaluado", "tipozona", `${evaluadosList_id}`],
  });

  const generoTexto =
    generoMap[queryEvaluado.data?.genero as string] || "No especificado";

  useEffect(() => {
    console.log(queryEvaluado.data);
    if (queryEvaluado.data) {
      const fechaNac = queryEvaluado.data.fecha_nacimiento || "";
      setFechaNacimiento(fechaNac);

      const nuevaEdad = calcularEdad(fechaNac);
      setEdad(nuevaEdad);

      reset({
        nombre: queryEvaluado.data.nombre || "",
        apellido_paterno: queryEvaluado.data.apellido_paterno || "",
        apellido_materno: queryEvaluado.data.apellido_materno || "",
        rfc: queryEvaluado.data.rfc || "",
        genero: queryEvaluado.data.genero || "",
        estado_civil: queryEvaluado.data.estado_civil || "",
        escolaridad: queryEvaluado.data.escolaridad || "",
        fecha_nacimiento: queryEvaluado.data.fecha_nacimiento || "",
        curp: queryEvaluado.data.curp || "",
        empresa: queryEvaluado.data.empresa?.nombre || "",
      });
    }
  }, [queryEvaluado.data, reset]);

  useEffect(() => {
    console.log("queryFecha.data:", queryFecha.data);

    if (queryFecha.data && queryFecha.data.length > 0) {
      const registroValido = queryFecha.data.find(
        (item) => item.fecha_bienesinmuebles
      );

      if (registroValido) {
        setValue("fecha_bienesinmuebles", registroValido.fecha_bienesinmuebles);
      }
    }
  }, [queryFecha.data, setValue]);

  useEffect(() => {
    console.log("queryAuto.data:", queryAuto.data);
    if (queryAuto.data && queryAuto.data.length > 0) {
      const auto = queryAuto.data[0];
      reset({
        marca_autobienes1: auto.marca_autobienes1 || "",
        modelo_autobienes1: auto.modelo_autobienes1 || "",
        precio_autobienes1: auto.precio_autobienes1 || "",
        liquidado_nombre_autobienes1: auto.liquidado_nombre_autobienes1 || "",
        marca_autobienes2: auto.marca_autobienes2 || "",
        modelo_autobienes2: auto.modelo_autobienes2 || "",
        precio_autobienes2: auto.precio_autobienes2 || "",
        liquidado_nombre_autobienes2: auto.liquidado_nombre_autobienes2 || "",
        marca_autobienes3: auto.marca_autobienes3 || "",
        modelo_autobienes3: auto.modelo_autobienes3 || "",
        precio_autobienes3: auto.precio_autobienes3 || "",
        liquidado_nombre_autobienes3: auto.liquidado_nombre_autobienes3 || "",
        marca_autobienes4: auto.marca_autobienes4 || "",
        modelo_autobienes4: auto.modelo_autobienes4 || "",
        precio_autobienes4: auto.precio_autobienes4 || "",
        liquidado_nombre_autobienes4: auto.liquidado_nombre_autobienes4 || "",
        marca_autobienes5: auto.marca_autobienes5 || "",
        modelo_autobienes5: auto.modelo_autobienes5 || "",
        precio_autobienes5: auto.precio_autobienes5 || "",
        liquidado_nombre_autobienes5: auto.liquidado_nombre_autobienes5 || "",
      });
    }
  }, [queryAuto.data, reset]);

  useEffect(() => {
    console.log("queryBienesRaices.data:", queryBienesRaices.data);
    if (queryBienesRaices.data && queryBienesRaices.data.length > 0) {
      const bienes = queryBienesRaices.data[0];
      reset({
        tipo_bienes1: bienes.tipo_bienes1 || "",
        ubicacion_bienes1: bienes.ubicacion_bienes1 || "",
        precio_bienes1: bienes.precio_bienes1 || "",
        liquidado_nombre_bienes1: bienes.liquidado_nombre_bienes1 || "",
        tipo_bienes2: bienes.tipo_bienes2 || "",
        ubicacion_bienes2: bienes.ubicacion_bienes2 || "",
        precio_bienes2: bienes.precio_bienes2 || "",
        liquidado_nombre_bienes2: bienes.liquidado_nombre_bienes2 || "",
        tipo_bienes3: bienes.tipo_bienes3 || "",
        ubicacion_bienes3: bienes.ubicacion_bienes3 || "",
        precio_bienes3: bienes.precio_bienes3 || "",
        liquidado_nombre_bienes3: bienes.liquidado_nombre_bienes3 || "",
        tipo_bienes4: bienes.tipo_bienes4 || "",
        ubicacion_bienes4: bienes.ubicacion_bienes4 || "",
        precio_bienes4: bienes.precio_bienes4 || "",
        liquidado_nombre_bienes4: bienes.liquidado_nombre_bienes4 || "",
        tipo_bienes5: bienes.tipo_bienes5 || "",
        ubicacion_bienes5: bienes.ubicacion_bienes5 || "",
        precio_bienes5: bienes.precio_bienes5 || "",
        liquidado_nombre_bienes5: bienes.liquidado_nombre_bienes5 || "",
      });
    }
  }, [queryBienesRaices.data, reset]);

  useEffect(() => {
    console.log("queryNotasBienes.data:", queryNotasBienes.data);
    if (queryNotasBienes.data && queryNotasBienes.data.length > 0) {
      const notas = queryNotasBienes.data[0];
      reset({
        nota_bienes1: notas.nota_bienes1 || "",
        nota_bienes2: notas.nota_bienes2 || "",
      });
    }
  }, [queryBienesRaices.data, reset]);

  useEffect(() => {
    console.log("querySituacionInmueble.data:", querySituacionInmueble.data);
    if (querySituacionInmueble.data && querySituacionInmueble.data.length > 0) {
      const situacion = querySituacionInmueble.data[0];
      reset({
        situacion_del_inmueble: situacion.situacion_del_inmueble || "",
      });
    }
  }, [queryBienesRaices.data, reset]);

  useEffect(() => {
    console.log("queryZonaInmueble.data:", queryZonaInmueble.data);
    if (queryZonaInmueble.data && queryZonaInmueble.data.length > 0) {
      const zona = queryZonaInmueble.data[0];
      reset({
        zona_del_inmueble: zona.zona_del_inmueble || "",
      });
    }
  }, [queryBienesRaices.data, reset]);

  useEffect(() => {
    console.log("queryTipoZona.data:", queryTipoZona.data);
    if (queryTipoZona.data && queryTipoZona.data.length > 0) {
      const tipozona = queryTipoZona.data[0];
      reset({
        tipo_de_zona: tipozona.tipo_de_zona || "",
      });
    }
  }, [queryBienesRaices.data, reset]);

  useEffect(() => {
    if (querySituacionInmueble.data) {
      setSituacionSeleccionada(
        querySituacionInmueble.data[0]?.situacion_del_inmueble || null
      );
    }
    if (queryZonaInmueble.data) {
      setNivelSocioeconomicoSeleccionado(
        queryZonaInmueble.data[0]?.zona_del_inmueble || null
      );
    }
    if (queryTipoZona.data) {
      setTipoZonaSeleccionado(queryTipoZona.data[0]?.tipo_de_zona || null);
    }
  }, [querySituacionInmueble.data, queryZonaInmueble.data, queryTipoZona.data]);

  if (queryEvaluado.isLoading) {
    return <Loading />;
  }

  const renderPage1 = () => (
    <div className="space-y-4 p-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="col-span-1 flex items-center">
          <Label htmlFor="fecha_bienesinmuebles" className={`mr-2 ${isDarkMode ? 'dark:text-gray-300' : 'text-gray-900'}`}>
            FECHA VISITA:
          </Label>
          <Controller
            name="fecha_bienesinmuebles"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                id="fecha_bienesinmuebles"
                name="fecha_bienesinmuebles"
                type="date"
                value={
                  queryFecha.data && queryFecha.data.length > 0
                    ? queryFecha.data[0].fecha_bienesinmuebles || ""
                    : ""
                }
                className={`w-32 ${isDarkMode ? "bg-gray-700 text-white" : "bg-white text-gray-700"}`}
              />
            )}
          />
        </div>
        <div className="col-span-2">
          <Label htmlFor="nombre" className={`${isDarkMode ? 'dark:text-gray-300' : 'text-gray-800'}`}>NOMBRE:</Label>
          <TextInput
            id="nombre"
            name="nombre"
            value={`${queryEvaluado.data?.nombre || ""} ${
              queryEvaluado.data?.apellido_paterno || ""
            } ${queryEvaluado.data?.apellido_materno || ""}`}
            onChange={handleInputChange}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        {/* Campo para RFC */}
        <div className="col-span-1">
          <Label htmlFor="rfc" className={`${isDarkMode ? 'dark:text-gray-300' : 'text-gray-800'}`}>RFC:</Label>
          <TextInput
            id="rfc"
            name="rfc"
            onChange={handleInputChange}
            value={`${queryEvaluado.data?.rfc || ""}`}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        {/* Edad */}
        <div className="col-span-1">
          <Label htmlFor="edad" className={`${isDarkMode ? 'dark:text-gray-300' : 'text-gray-800'}`}>EDAD:</Label>
          <TextInput
            id="edad"
            name="edad"
            value={edad !== null ? edad.toString() : ""}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="sexo" className={`${isDarkMode ? 'dark:text-gray-300' : 'text-gray-800'}`}>SEXO:</Label>
          <TextInput
            id="sexo"
            name="sexo"
            value={generoTexto}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="estadoCivil" className={`${isDarkMode ? 'dark:text-gray-300' : 'text-gray-800'}`}>ESTADO CIVIL:</Label>
          <TextInput
            id="estadoCivil"
            name="estadoCivil"
            onChange={handleInputChange}
            value={`${queryEvaluado.data?.estado_civil || ""}`}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="escolaridad" className={`${isDarkMode ? 'dark:text-gray-300' : 'text-gray-800'}`}>ESCOLARIDAD:</Label>
          <TextInput
            id="escolaridad"
            name="escolaridad"
            onChange={handleInputChange}
            value={`${queryEvaluado.data?.escolaridad || ""}`}
            disabled={true}
            className={`${isDarkMode ? "bg-gray-700 text-white border-gray-600" : "bg-gray-100 text-gray-700 border-gray-300"}`}
          />
        </div>
        <div className="col-span-1 flex items-center">
          <Label htmlFor="fechaNacimiento" className={`mr-2 ${isDarkMode ? 'dark:text-gray-300' : 'text-gray-800'}`}>
            FECHA DE NACIMIENTO:
          </Label>
          <TextInput
            id="fechaNacimiento"
            name="fechaNacimiento"
            value={`${queryEvaluado.data?.fecha_nacimiento || ""}`}
            disabled={true}
            type="date"
            onChange={handleInputChange}
            className={`w-32 ${isDarkMode ? 'dark:bg-gray-700 dark:border-gray-600 dark:text-white' : 'bg-gray-50'}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="curp" className={`${isDarkMode ? 'dark:text-gray-300' : 'text-gray-900'}`}>CURP:</Label>
          <TextInput
            id="curp"
            name="curp"
            onChange={handleInputChange}
            value={queryEvaluado.data?.curp || ""}
            disabled={true}
            className={`${isDarkMode ? 'dark:bg-gray-700 dark:border-gray-600 dark:text-white' : 'bg-gray-50'}`}
          />
        </div>
        <div className="col-span-1">
          <Label htmlFor="empresa" className={`${isDarkMode ? 'dark:text-gray-300' : 'text-gray-900'}`}>EMPRESA:</Label>
          <TextInput
            id="empresa_id"
            name="empresa"
            value={queryEvaluado.data?.empresa_id?.nombre || ""}
            onChange={handleInputChange}
            disabled={true}
            className={`${isDarkMode ? 'dark:bg-gray-700 dark:border-gray-600 dark:text-white' : 'bg-gray-50'}`}
          />
        </div>
      </div>
    </div>
  );

  const renderPage2 = () => (
    <div className={`space-y-4 p-4 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white'}`}>
        <div className={`p-4 ${isDarkMode ? 'bg-gray-800' : 'bg-customBlue'}`}>
            <h2 className="font-bold mb-2">BIENES INMUEBLES / MUEBLES</h2>
            <div className="space-y-4">
                {[...Array(5)].map((_, index) => (
                    <div key={index} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-200'}`}>
                        <h3 className="font-bold mb-2">AUTOMÓVIL {index + 1}</h3>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor={`marca_autobienes${index + 1}`} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>MARCA</Label>
                                <Controller
                                    name={`marca_autobienes${index + 1}`}
                                    control={control}
                                    defaultValue={
                                        queryAuto.data && queryAuto.data.length > 0
                                            ? queryAuto.data[0][
                                                `marca_autobienes${
                                                    index + 1
                                                }` as keyof AutomovilesBienes
                                            ] || ""
                                            : ""
                                    }
                                    render={({ field }) => (
                                        <TextInput
                                            id={`marca_autobienes${index + 1}`}
                                            {...field}
                                            className={`${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'}`}
                                        />
                                    )}
                                />
                            </div>
                            <div>
                                <Label htmlFor={`modelo_autobienes${index + 1}`} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                    MODELO
                                </Label>
                                <Controller
                                    name={`modelo_autobienes${index + 1}`}
                                    control={control}
                                    defaultValue={
                                        queryAuto.data && queryAuto.data.length > 0
                                            ? queryAuto.data[0][
                                                `modelo_autobienes${
                                                    index + 1
                                                }` as keyof AutomovilesBienes
                                            ] || ""
                                            : ""
                                    }
                                    render={({ field }) => (
                                        <TextInput
                                            id={`modelo_autobienes${index + 1}`}
                                            {...field}
                                            className={`${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'}`}
                                        />
                                    )}
                                />
                            </div>
                            <div>
                                <Label htmlFor={`precio_autobienes${index + 1}`} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                    PRECIO ESTIMADO
                                </Label>
                                <Controller
                                    name={`precio_autobienes${index + 1}`}
                                    control={control}
                                    defaultValue={
                                        queryAuto.data && queryAuto.data.length > 0
                                            ? queryAuto.data[0][
                                                `precio_autobienes${
                                                    index + 1
                                                }` as keyof AutomovilesBienes
                                            ] || ""
                                            : ""
                                    }
                                    render={({ field }) => (
                                        <TextInput
                                            id={`precio_autobienes${index + 1}`}
                                            {...field}
                                            className={`${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'}`}
                                        />
                                    )}
                                />
                            </div>
                            <div>
                                <Label htmlFor={`liquidado_nombre_autobienes${index + 1}`} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                                    LIQUIDADO A NOMBRE DE / CRÉDITO
                                </Label>
                                <Controller
                                    name={`liquidado_nombre_autobienes${index + 1}`}
                                    control={control}
                                    defaultValue={
                                        queryAuto.data && queryAuto.data.length > 0
                                            ? queryAuto.data[0][
                                                `precio_autobienes${
                                                    index + 1
                                                }` as keyof AutomovilesBienes
                                            ] || ""
                                            : ""
                                    }
                                    render={({ field }) => (
                                        <TextInput
                                            id={`liquidado_nombre_autobienes${index + 1}`}
                                            {...field}
                                            className={`${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'}`}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>
  );

  const renderPage3 = () => (
    <div className={`space-y-4 p-4 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white'}`}>
      <h2 className="font-bold mb-2">BIENES INMUEBLES / MUEBLES</h2>
      <div className="space-y-4">
        {[...Array(5)].map((_, index) => (
          <div key={index} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-blue-200'}`}>
            <h3 className="font-bold mb-2">BIENES RAÍCES {index + 1}</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor={`tipo_bienes${index + 1}`} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>TIPO</Label>
                <Controller
                  name={`tipo_bienes${index + 1}`}
                  control={control}
                  defaultValue={
                    queryBienesRaices.data && queryBienesRaices.data.length > 0
                      ? queryBienesRaices.data[0][
                          `tipo_bienes${index + 1}` as keyof BienesRaices
                        ] || ""
                      : ""
                  }
                  render={({ field }) => (
                    <TextInput id={`tipo_bienes${index + 1}`} {...field}
                      className={`${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'}`}
                    />
                  )}
                />
              </div>
              <div>
                <Label htmlFor={`ubicacion_bienes${index + 1}`} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                  UBICACIÓN
                </Label>
                <Controller
                  name={`ubicacion_bienes${index + 1}`}
                  control={control}
                  defaultValue={
                    queryBienesRaices.data && queryBienesRaices.data.length > 0
                      ? queryBienesRaices.data[0][
                          `ubicacion_bienes${index + 1}` as keyof BienesRaices
                        ] || ""
                      : ""
                  }
                  render={({ field }) => (
                    <TextInput id={`ubicacion_bienes${index + 1}`} {...field}
                      className={`${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'}`}
                    />
                  )}
                />
              </div>
              <div>
                <Label htmlFor={`precio_bienes${index + 1}`} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                  PRECIO ESTIMADO
                </Label>
                <Controller
                  name={`precio_bienes${index + 1}`}
                  control={control}
                  defaultValue={
                    queryBienesRaices.data && queryBienesRaices.data.length > 0
                      ? queryBienesRaices.data[0][
                          `precio_bienes${index + 1}` as keyof BienesRaices
                        ] || ""
                      : ""
                  }
                  render={({ field }) => (
                    <TextInput id={`precio_bienes${index + 1}`} {...field}
                      className={`${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'}`}
                    />
                  )}
                />
              </div>
              <div>
                <Label htmlFor={`liquidado_nombre_bienes${index + 1}`} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                  LIQUIDADO A NOMBRE DE
                </Label>
                <Controller
                  name={`liquidado_nombre_bienes${index + 1}`}
                  control={control}
                  defaultValue={
                    queryBienesRaices.data && queryBienesRaices.data.length > 0
                      ? queryBienesRaices.data[0][
                          `liquidado_nombre_bienes${
                            index + 1
                          }` as keyof BienesRaices
                        ] || ""
                      : ""
                  }
                  render={({ field }) => (
                    <TextInput
                      id={`liquidado_nombre_bienes${index + 1}`}
                      {...field}
                      className={`${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'bg-white text-gray-900 border-gray-300'}`}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div>
        <Label htmlFor="notas" className={`${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>NOTAS:</Label>
        <Controller
          name="nota_bienes1"
          control={control}
          defaultValue={
            queryNotasBienes.data && queryNotasBienes.data.length > 0
              ? queryNotasBienes.data[0].nota_bienes1 || ""
              : ""
          }
          render={({ field }) => (
            <textarea
              id="nota_bienes1"
              {...field}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = "auto";
                target.style.height = `${target.scrollHeight}px`;
              }}
              style={{ height: "auto", overflow: "hidden" }}
              className={`block w-full mt-2 p-3 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-600 text-white border-gray-500' : 'border-gray-300'}`}
              placeholder="Escribe aquí tus notas..."
            />
          )}
        />
      </div>
    </div>
  );

  const renderPage4 = () => (
    <div className={`space-y-6 p-6 rounded-lg shadow-md ${isDarkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
        <div className="mb-4">
            <div className={`p-2 rounded-t-lg ${isDarkMode ? 'bg-blue-800' : 'bg-customBlue text-white'}`}>
                <h2 className="font-bold text-lg">SITUACIÓN DEL INMUEBLE</h2>
            </div>
            <div className={`grid grid-cols-3 gap-4 p-4 rounded-b-lg ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-blue-600'}`} style={{ border: '1px solid' }}>
                {[
                    "PROPIO",
                    "RENTADO",
                    "HIPOTECADO",
                    "PRESTADO",
                    "COMPARTIDO",
                    "CASA SOLA",
                    "DEPARTAMENTO",
                    "CONDOMINIO",
                    "CASA DUPLEX",
                    "VECINDAD",
                ].map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                        <Checkbox
                            id={`situacion${option}`}
                            checked={situacionSeleccionada === option}
                            onChange={() => setSituacionSeleccionada(option)}
                        />
                        <Label htmlFor={`situacion${option}`} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {option}
                        </Label>
                    </div>
                ))}
            </div>
        </div>

        <div className="mb-4">
            <div className={`p-2 rounded-t-lg ${isDarkMode ? 'bg-blue-800' : 'bg-customBlue text-white'}`}>
                <h2 className="font-bold text-lg">ZONA</h2>
            </div>
            <div className={`grid grid-cols-3 gap-4 p-4 rounded-b-lg ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-blue-600'}`} style={{ border: '1px solid' }}>
                {["ALTA", "MEDIA ALTA", "MEDIA", "MEDIA BAJA", "BAJA"].map(
                    (option) => (
                        <div key={option} className="flex items-center space-x-2">
                            <Checkbox
                                id={`nivelSocioeconomico${option}`}
                                checked={nivelSocioeconomicoSeleccionado === option}
                                onChange={() => setNivelSocioeconomicoSeleccionado(option)}
                            />
                            <Label
                                htmlFor={`nivelSocioeconomico${option}`}
                                className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                            >
                                {option}
                            </Label>
                        </div>
                    )
                )}
            </div>
        </div>

        <div className="mb-4">
            <div className={`p-2 rounded-t-lg ${isDarkMode ? 'bg-blue-800' : 'bg-customBlue text-white'}`}>
                <h2 className="font-bold text-lg">TIPO DE ZONA</h2>
            </div>
            <div className={`grid grid-cols-3 gap-4 p-4 rounded-b-lg ${isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-blue-600'}`} style={{ border: '1px solid' }}>
                {["URBANA", "RURAL", "SUBURBANA"].map((option) => (
                    <div key={option} className="flex items-center space-x-2">
                        <Checkbox
                            id={`tipoZona${option}`}
                            checked={tipoZonaSeleccionado === option}
                            onChange={() => setTipoZonaSeleccionado(option)}
                        />
                        <Label htmlFor={`tipoZona${option}`} className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {option}
                        </Label>
                    </div>
                ))}
            </div>
        </div>

        <div className="mb-4">
            <Label
                htmlFor="notasAdicionales"
                className={`block font-bold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}
            >
                NOTAS:
            </Label>
            <Controller
                name="nota_bienes2"
                control={control}
                defaultValue={
                    queryNotasBienes.data && queryNotasBienes.data.length > 0
                        ? queryNotasBienes.data[0].nota_bienes2 || ""
                        : ""
                }
                render={({ field }) => (
                    <div>
                        <textarea
                            id="nota_bienes2"
                            {...field}
                            onInput={(e) => {
                                const target = e.target as HTMLTextAreaElement;
                                target.style.height = "auto";
                                target.style.height = `${target.scrollHeight}px`;
                            }}
                            style={{ height: "auto", overflow: "hidden" }}
                            className={`block w-full mt-2 p-3 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'border-gray-300'}`}
                            placeholder="Escribe aquí tus notas..."
                        />
                    </div>
                )}
            />
        </div>

        <div className={`mt-4 p-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
            <div className="text-center">
                <p className={`text-left text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Bajo protesta de decir verdad, hago constar que los datos
                    proporcionados son verdaderos.
                </p>
                <p className={`text-left text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    Asimismo, autorizo a la empresa SKA TRACKING AND SECURITY S.A. de
                    C.V. para que proporcione toda la Información, documentos y todos
                    los datos que derive de las evaluaciones de pruebas de confianza que
                    se me realicen.
                </p>
            </div>
        </div>
    </div>
  );
return (
    <div className={`min-h-screen ${isDarkMode ? "bg-gray-900" : "bg-gray-50"}`}>
      <div className={`container mx-auto p-4`}>
        <form
          onSubmit={handleSubmit((data) => {
            console.log(data);

            const formData = {
              ...data,
              situacion_del_inmueble: situacionSeleccionada,
              zona_del_inmueble: nivelSocioeconomicoSeleccionado,
              tipo_de_zona: tipoZonaSeleccionado,
            };

            // Mutación para guardar la Fecha
            mutateFecha.mutate(
              {
                data: formData,
                tipo: "put",
              },
              {
                onSuccess: () => {
                  // Mutación para guardar Automóviles Bienes
                  mutateAutomovilBienes.mutate(
                    {
                      data: formData,
                      tipo: "put",
                    },
                    {
                      onSuccess: () => {
                        // Mutación para guardar Bienes Raíces
                        mutateBienesRaices.mutate(
                          {
                            data: formData,
                            tipo: "put",
                          },
                          {
                            onSuccess: () => {
                              // Mutación para guardar Notas de Bienes Inmuebles
                              mutateNotasBienes.mutate(
                                {
                                  data: formData,
                                  tipo: "put",
                                },
                                {
                                  onSuccess: () => {
                                    // Luego guardar Situación del Inmueble
                                    mutateSituacionInmueble.mutate(
                                      {
                                        data: formData,
                                        tipo: "put",
                                      },
                                      {
                                        onSuccess: () => {
                                          // Luego guardar Zona del Inmueble
                                          mutateZonaInmueble.mutate(
                                            {
                                              data: formData,
                                              tipo: "put",
                                            },
                                            {
                                              onSuccess: () => {
                                                // Por último guardar Tipo de Zona
                                                mutateTipoZona.mutate(
                                                  {
                                                    data: formData,
                                                    tipo: "put",
                                                  },
                                                  {
                                                    onSuccess: () => {
                                                      navigate(
                                                        `/domiciliaria/${evaluadosList_id}`
                                                      );
                                                    },
                                                    onError: (error) => {
                                                      console.error(
                                                        "Error al guardar Tipo de Zona:",
                                                        error
                                                      );
                                                    },
                                                  }
                                                );
                                              },
                                              onError: (error) => {
                                                console.error(
                                                  "Error al guardar Zona del Inmueble:",
                                                  error
                                                );
                                              },
                                            }
                                          );
                                        },
                                        onError: (error) => {
                                          console.error(
                                            "Error al guardar Situación del Inmueble:",
                                            error
                                          );
                                        },
                                      }
                                    );
                                  },
                                  onError: (error) => {
                                    console.error(
                                      "Error al guardar Notas de Bienes Inmuebles:",
                                      error
                                    );
                                  },
                                }
                              );
                            },
                            onError: (error) => {
                              console.error(
                                "Error al guardar Bienes Raíces:",
                                error
                              );
                            },
                          }
                        );
                      },
                      onError: (error) => {
                        console.error(
                          "Error al guardar Automóviles Bienes:",
                          error
                        );
                      },
                    }
                  );
                },
                onError: (error) => {
                  console.error("Error al guardar Fecha:", error);
                },
              }
            );
          })}
          className={`max-w-4xl mx-auto rounded-lg shadow-lg overflow-hidden ${isDarkMode ? 'bg-gray-700' : 'bg-white'}`}
        >
          {renderHeader()}

          {currentPage === 1 && renderPage1()}
          {currentPage === 2 && renderPage2()}
          {currentPage === 3 && renderPage3()}
          {currentPage === 4 && renderPage4()}

          <div className={`mt-6 p-4 flex justify-between ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
            {currentPage < 2 && (
              <Button
                color="red"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate(`/domiciliaria/${evaluadosList_id}`);
                }}
                className={`${isDarkMode ? 'bg-red-700 text-white' : 'bg-red-500 text-white'}`}
              >
                Cancelar
              </Button>
            )}
            {currentPage > 1 && (
              <button
                type="button"
                className={`rounded px-4 py-2 ${isDarkMode ? 'bg-violet-600 text-white' : 'bg-violet-300 text-white-700'}`}
                onClick={() => {
                  handlePageChange(currentPage - 1);
                  window.scrollTo(0, 0);
                }}
              >
                Anterior
              </button>
            )}
            {currentPage < 4 && (
              <button
                type="button"
                className={`rounded px-4 py-2 ${isDarkMode ? 'bg-blue-700 text-white' : 'bg-blue-500 text-white'}`}
                onClick={() => {
                  handlePageChange(currentPage + 1);
                  window.scrollTo(0, 0);
                }}
              >
                Siguiente
              </button>
            )}
            {currentPage === 4 && (
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className={`rounded px-4 py-2 ${isDarkMode ? 'bg-green-700 text-white' : 'bg-green-500 text-white'}`}
                >
                  Guardar
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
